import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, Layout } from 'antd';
import AppNotificationBridge from './AppNotificationBridge';
import Footer from 'components/Footer/Loadable';
import PrivacyPolicy from 'components/PrivacyPolicy/Loadable';
import TermsAndConditions from 'components/TermsAndConditions/Loadable';
import SignupSuccess from 'components/SignupSuccess/Loadable';
import HomePage from 'containers/HomePage/Loadable';
import Auth from 'containers/Auth/Loadable';
import Header from 'containers/Header/Loadable';
import Favorite from 'containers/Favorite/Loadable';
import OrderHistory from 'containers/OrderHistory/Loadable';
import OrderDetail from 'containers/OrderDetail/Loadable';
import CatererDetail from 'containers/CatererDetail/Loadable';
import DecoratorDetail from 'containers/DecoratorDetail/Loadable';
import PhotographerDetail from 'containers/PhotographerDetail/Loadable';
import VenueDetail from 'containers/VenueDetail/Loadable';
import Profile from 'containers/Profile/Loadable';
import ContactUs from 'containers/ContactUs/Loadable';
import SearchResultView from 'containers/SearchResultView/Loadable';
import Faqs from 'containers/Faqs/Loadable';
import Verify from 'containers/VerifyEmail/Loadable';
import VerifyMobileNumber, {
  VerifySuccess,
} from 'containers/VerifyMobileNumber/Loadable';
import ResetPassword from 'containers/ResetPassword/Loadable';
import NotFoundPage from 'containers/NotFoundPage/Loadable';
import ReactGA from 'react-ga4';
import HelpCenter from '../../components/HelpCenter/Loadable';
import HelpMain from '../HelpMain/Loadable';
import ReviewSuccess from '../../components/ReviewSuccess/Loadable';
import Reviews from '../Reviews/Loadable';
import OrderConfirmation from '../../components/OrderConfirmation/Loadable';
import StripePaymentPage from '../StripePaymentPage/Loadable';
import appConfig from '../../siteConfigs/appConfig';
import {
  LEGACY_HELP_CENTER_PREFIX,
  LEGACY_PATH_REDIRECTS,
  PATHS,
} from '../../siteConfigs/paths';
import { useSessionBootstrap } from '../../hooks/useSessionBootstrap';

import GlobalStyle from '../../global-styles';
import AppContent from './AppContent';
import { MobileResultsSearchProvider } from '../Header/MobileResultsSearchContext';

function LegacyPathRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

LegacyPathRedirect.propTypes = {
  to: PropTypes.string.isRequired,
};

function LegacyHelpCenterRedirect() {
  const location = useLocation();
  const suffix = location.pathname
    .replace(new RegExp(`^${LEGACY_HELP_CENTER_PREFIX}/?`), '')
    .replace(/^\//, '');
  const base = PATHS.helpCenter;
  const target = suffix ? `${base}/${suffix}` : base;
  return (
    <Navigate to={`${target}${location.search}${location.hash}`} replace />
  );
}

export default function App() {
  useSessionBootstrap();

  useEffect(() => {
    ReactGA.initialize(appConfig.analytics.googleTrackingId);
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname + window.location.search,
    });
  });

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FF385C',
          // Soft neutral focus for inputs — avoid harsh black / error-like pink.
          controlOutline: 'rgba(0, 0, 0, 0.04)',
          colorPrimaryBorder: '#b0b0b0',
          colorPrimaryHover: '#E61E4D',
          // Avoid Ant Design default blue for links / info accents on mobile.
          colorLink: '#222222',
          colorLinkHover: '#000000',
          colorLinkActive: '#000000',
          colorInfo: '#222222',
        },
        components: {
          Input: {
            activeBorderColor: '#b0b0b0',
            hoverBorderColor: '#c4c4c4',
            activeShadow: '0 0 0 2px rgba(0, 0, 0, 0.04)',
          },
          InputNumber: {
            activeBorderColor: '#b0b0b0',
            hoverBorderColor: '#c4c4c4',
            activeShadow: '0 0 0 2px rgba(0, 0, 0, 0.04)',
          },
          Select: {
            activeBorderColor: '#b0b0b0',
            hoverBorderColor: '#c4c4c4',
            optionSelectedBg: '#f7f7f7',
          },
          DatePicker: {
            activeBorderColor: '#b0b0b0',
            hoverBorderColor: '#c4c4c4',
            activeShadow: '0 0 0 2px rgba(0, 0, 0, 0.04)',
            cellActiveWithRangeBg: '#f7f7f7',
            cellHoverWithRangeBg: '#f7f7f7',
            cellHoverBg: '#f7f7f7',
          },
          Cascader: {
            activeBorderColor: '#b0b0b0',
            hoverBorderColor: '#c4c4c4',
            optionSelectedBg: '#f7f7f7',
          },
        },
      }}
    >
      <AntdApp>
        <AppNotificationBridge />
        <MobileResultsSearchProvider>
          <Layout className="site-layout">
            <Auth />
            <Header />
            <AppContent>
              <Routes>
                <Route path={PATHS.home} element={<HomePage />} />
                <Route path={`${PATHS.venues}/:id`} element={<VenueDetail />} />
                <Route path={PATHS.venues} element={<SearchResultView />} />
                <Route
                  path={PATHS.orderConfirmation}
                  element={<OrderConfirmation />}
                />
                <Route path={PATHS.payment} element={<StripePaymentPage />} />
                <Route path={PATHS.orderHistory} element={<OrderHistory />} />
                <Route path={PATHS.profile} element={<Profile />} />
                <Route path={PATHS.contactUs} element={<ContactUs />} />
                <Route path={PATHS.faqs} element={<Faqs />} />
                <Route path={PATHS.writeReview} element={<Reviews />} />
                <Route path={PATHS.helpMain} element={<HelpMain />} />
                <Route path={PATHS.reviewSuccess} element={<ReviewSuccess />} />
                <Route
                  path={`${PATHS.helpCenter}/*`}
                  element={<HelpCenter />}
                />
                <Route
                  path={PATHS.termsAndConditions}
                  element={<TermsAndConditions />}
                />
                <Route path={PATHS.privacyPolicy} element={<PrivacyPolicy />} />
                <Route path={PATHS.orderDetail} element={<OrderDetail />} />
                <Route path={PATHS.caterers} element={<SearchResultView />} />
                <Route
                  path={`${PATHS.caterers}/:id`}
                  element={<CatererDetail />}
                />
                <Route path={PATHS.decorators} element={<SearchResultView />} />
                <Route
                  path={`${PATHS.decorators}/:id`}
                  element={<DecoratorDetail />}
                />
                <Route
                  path={PATHS.photographers}
                  element={<SearchResultView />}
                />
                <Route
                  path={`${PATHS.photographers}/:id`}
                  element={<PhotographerDetail />}
                />
                <Route path={PATHS.verify} element={<Verify />} />
                <Route
                  path={PATHS.verifyMobileNumber}
                  element={<VerifyMobileNumber />}
                />
                <Route
                  path={PATHS.verificationSuccess}
                  element={<VerifySuccess />}
                />
                <Route
                  path={PATHS.accountRegister}
                  element={<SignupSuccess />}
                />
                <Route path={PATHS.resetPassword} element={<ResetPassword />} />
                <Route path={PATHS.favorite} element={<Favorite />} />
                {LEGACY_PATH_REDIRECTS.map(({ from, to }) => (
                  <Route
                    key={from}
                    path={from}
                    element={<LegacyPathRedirect to={to} />}
                  />
                ))}
                <Route
                  path={`${LEGACY_HELP_CENTER_PREFIX}/*`}
                  element={<LegacyHelpCenterRedirect />}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppContent>
            <Footer />
          </Layout>
        </MobileResultsSearchProvider>
        <GlobalStyle />
      </AntdApp>
    </ConfigProvider>
  );
}
